/**
 * VEXOS Marketing & Collateral Tab.
 *
 * Carryover from the legacy TRP calculator's "Marketing Strategy & Lead
 * Generation" + "Collateral Suite" modules, restyled to the VEXOS dark
 * luxury aesthetic. Lives inside VexosClientCalculator's tabbed view.
 *
 * State + math are owned by the parent so the sidebar / payment structure
 * / save payload all see the same numbers. This file is purely the UI for
 * the two modules.
 */
import React, { useState } from "react";
import { Plus, Minus, ChevronDown, Megaphone, Sparkles, Download, AlertTriangle } from "lucide-react";
import {
  BRANDING_SHORT_DESC,
  BRANDING_PROMISE,
  BRANDING_STAGES,
  BRANDING_DELIVERABLES,
  BRANDING_PROCESS,
  BRANDING_DURATION_NOTE,
  BRANDING_INCLUSIONS,
} from "./brandingPackage";

// ─── Helpers ───────────────────────────────────────────────────────────
const fmtUSD = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

const TierTile = ({ active, label, sub, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left rounded-xl p-4 transition-all"
    style={{
      background: active ? "rgba(143,167,155,0.12)" : "rgba(255,255,255,0.025)",
      border: `1px solid ${active ? "rgba(143,167,155,0.55)" : "rgba(255,255,255,0.06)"}`,
      color: active ? "#ffffff" : "rgba(255,255,255,0.78)",
    }}
    data-testid={testId}
  >
    <p style={{ fontSize: "13px", fontWeight: 400 }}>{label}</p>
    {sub && <p className="mt-1 text-white/45" style={{ fontSize: "11px" }}>{sub}</p>}
  </button>
);

/**
 * Click-to-edit campaign fee. The rate card resolves a fee from the Lead Gen
 * tier; this lets a rep replace it for one quote when the deal is negotiated.
 *
 * Writes the override to the parent's `marketing` state, which is where
 * `marketingMath` picks it up — so the sidebar, payment milestones, saved
 * quote, web proposal and PDF all follow from that one place. An empty field
 * clears the override and the tier's rate comes back.
 *
 * Internal surface: the amber treatment and the struck-through rate-card value
 * are for the rep. Nothing about the override reaches the client's proposal.
 */
const EditableFee = ({ value, auto, override, onCommit, suffix = "", testId }) => {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const overridden = override !== null && override !== undefined;

  const commit = () => {
    setEditing(false);
    const raw = draft.replace(/[^0-9.]/g, "");
    const n = Number(raw);
    if (raw === "" || !Number.isFinite(n) || n < 0) { onCommit(null); return; }
    onCommit(Math.max(0, Math.round(n)));
  };

  if (editing) {
    return (
      <span className="flex items-center justify-end gap-1">
        <span
          className="flex items-center rounded-md px-2"
          style={{ border: "1px solid rgba(251,191,36,0.45)", background: "rgba(251,191,36,0.07)" }}
        >
          <span className="text-white/40 text-[11px] mr-0.5">$</span>
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            value={draft}
            placeholder={String(Math.round(Number(auto) || 0))}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setEditing(false); }
            }}
            className="w-[72px] bg-transparent outline-none py-1 text-[12px] text-white text-right tabular-nums placeholder:text-white/25"
            data-testid={`${testId}-input`}
          />
        </span>
      </span>
    );
  }

  return (
    <span className="flex items-center justify-end gap-1.5">
      <button
        type="button"
        onClick={() => { setDraft(overridden ? String(override) : ""); setEditing(true); }}
        title="Click to set this fee by hand"
        className="tabular-nums underline decoration-dotted decoration-white/20 underline-offset-4 hover:decoration-white/50 transition-colors"
        style={{ color: overridden ? "rgb(253,230,138)" : "#ffffff", fontSize: "12px" }}
        data-testid={testId}
      >
        {fmtUSD(value)}{suffix}
      </button>
      {overridden && (
        <button
          type="button"
          onClick={() => onCommit(null)}
          title="Restore the rate-card fee"
          className="text-white/30 hover:text-white/70 transition-colors leading-none"
          style={{ fontSize: "13px" }}
          data-testid={`${testId}-reset`}
        >
          ×
        </button>
      )}
    </span>
  );
};

const SectionHeader = ({ Icon, title, right, open, onToggle, testId }) => (
  <div className="w-full flex items-center justify-between gap-4 py-4" data-testid={testId}>
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-3 flex-1 text-left"
      data-testid={`${testId}-collapse-btn`}
    >
      <span
        className="inline-flex items-center justify-center w-8 h-8 rounded-full"
        style={{ background: "rgba(143,167,155,0.12)", border: "1px solid rgba(143,167,155,0.35)", color: "#8FA79B" }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <p className="text-white" style={{ fontSize: "16px", fontWeight: 400 }}>{title}</p>
    </button>
    <div className="flex items-center gap-3">
      {right}
      <button
        type="button"
        onClick={onToggle}
        className="p-1"
        aria-label={open ? "Collapse section" : "Expand section"}
        data-testid={`${testId}-chevron-btn`}
      >
        <ChevronDown
          className="w-4 h-4 text-white/55 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>
    </div>
  </div>
);

const Toggle = ({ value, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!value)}
    className="relative inline-block w-11 h-6 rounded-full transition-colors"
    style={{ background: value ? "#8FA79B" : "rgba(255,255,255,0.12)" }}
    data-testid={testId}
    role="switch"
    aria-checked={value}
  >
    <span
      className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
      style={{ left: value ? "22px" : "2px" }}
    />
  </button>
);

const Slider = ({ label, value, min, max, step = 1, onChange, suffix, testId }) => {
  // Local string state so the rep can clear the field and re-type without
  // the controlled-input fighting their keystrokes. We commit the parsed
  // number up to the parent on every change (clamped on blur).
  const [draft, setDraft] = React.useState(String(value ?? min ?? 0));
  React.useEffect(() => {
    // Sync from parent when the slider drags or the value is reset
    // externally — but only when the field isn't currently focused.
    if (document.activeElement?.dataset?.testid !== `${testId}-input`) {
      setDraft(String(value ?? min ?? 0));
    }
  }, [value, min, testId]);

  const commit = (raw) => {
    if (raw === "" || raw == null) {
      setDraft(String(value));
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) { setDraft(String(value)); return; }
    const clamped = Math.min(max, Math.max(min, step < 1 ? n : Math.round(n)));
    onChange(clamped);
    setDraft(String(clamped));
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-white/65" style={{ fontSize: "12px" }}>{label}</p>
        <div className="flex items-baseline gap-0.5">
          <input
            type="number"
            inputMode="decimal"
            min={min}
            max={max}
            step={step}
            value={draft}
            onChange={(e) => {
              const v = e.target.value;
              setDraft(v);
              // Live-update the slider as the rep types — clamp only on blur.
              if (v !== "" && Number.isFinite(Number(v))) {
                const n = Number(v);
                if (n >= min && n <= max) onChange(step < 1 ? n : Math.round(n));
              }
            }}
            onFocus={(e) => e.target.select()}
            onBlur={(e) => commit(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`${testId}-input`}
            className="text-white bg-transparent text-right tabular-nums rounded px-1 border border-transparent focus:border-[#8FA79B]/60 focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            style={{ fontSize: "14px", fontWeight: 400, width: "60px" }}
          />
          {suffix ? (
            <span className="text-white tabular-nums" style={{ fontSize: "14px", fontWeight: 400 }}>{suffix}</span>
          ) : null}
        </div>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#8FA79B]"
        data-testid={testId}
      />
      <div className="flex justify-between mt-1 text-[10px] text-white/30">
        <span>{min}{suffix || ""}</span>
        <span>{max}{suffix || ""}</span>
      </div>
    </div>
  );
};

const ItemRow = ({ name, desc, price, value, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-white/[0.05]">
    <div className="flex-1 min-w-0">
      <p className="text-white" style={{ fontSize: "13px", fontWeight: 400 }}>{name}</p>
      {desc && <p className="text-white/45 mt-0.5" style={{ fontSize: "11px", lineHeight: 1.5 }}>{desc}</p>}
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <span className="text-white/75 tabular-nums" style={{ fontSize: "13px" }}>{fmtUSD(price)}</span>
      <Toggle value={!!value} onChange={onChange} testId={testId} />
    </div>
  </div>
);

const StepperRow = ({ name, desc, price, unit = "item", value, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-white/[0.05]">
    <div className="flex-1 min-w-0">
      <p className="text-white" style={{ fontSize: "13px", fontWeight: 400 }}>{name}</p>
      {desc && <p className="text-white/45 mt-0.5" style={{ fontSize: "11px", lineHeight: 1.5 }}>{desc}</p>}
    </div>
    <div className="flex items-center gap-3 flex-shrink-0">
      <span className="text-white/65" style={{ fontSize: "12px" }}>{fmtUSD(price)} /{unit}</span>
      <div className="inline-flex items-center gap-1 bg-white/[0.04] border border-white/10 rounded-full p-0.5">
        <button onClick={() => onChange(Math.max(0, (Number(value) || 0) - 1))} className="p-1.5 text-white/65 hover:text-white" data-testid={`${testId}-minus`}>
          <Minus className="w-3 h-3" />
        </button>
        <span className="min-w-[24px] text-center text-white tabular-nums" style={{ fontSize: "12px" }} data-testid={`${testId}-value`}>{Number(value) || 0}</span>
        <button onClick={() => onChange((Number(value) || 0) + 1)} className="p-1.5 text-white/65 hover:text-white" data-testid={`${testId}-plus`}>
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  </div>
);


/**
 * The Branding & Conceptual Package sells as one $10k line, so the menu has
 * to carry its own explanation — a rep can't talk a client through a comma-
 * separated list, and the client reading the proposal later has nothing else
 * to go on. Same toggle and price treatment as `ItemRow`, with the eight
 * deliverables one click away. Content lives in `brandingPackage.js`; this
 * component only lays it out.
 */
const BrandingPackageRow = ({ price, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/[0.05]" data-testid="col-branding-package-row">
      <div className="flex items-start justify-between gap-4 py-3">
        <div className="flex-1 min-w-0">
          <p className="text-white" style={{ fontSize: "13px", fontWeight: 400 }}>Branding &amp; Conceptual Package</p>
          <p className="text-white/45 mt-0.5" style={{ fontSize: "11px", lineHeight: 1.5 }}>{BRANDING_SHORT_DESC}</p>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-1.5 inline-flex items-center gap-1 text-[#8FA79B] hover:text-[#a9bdb3] transition-colors"
            style={{ fontSize: "11px" }}
            data-testid="col-branding-disclosure"
            aria-expanded={open}
          >
            {open ? "Hide" : "What's included"} · {BRANDING_DELIVERABLES.length} deliverables
            <ChevronDown className="w-3 h-3 transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
          </button>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-white/75 tabular-nums" style={{ fontSize: "13px" }}>{fmtUSD(price)}</span>
          <Toggle value={!!value} onChange={onChange} testId={testId} />
        </div>
      </div>

      {open && (
        <div className="pb-5 pl-0 md:pl-3 space-y-5" data-testid="col-branding-details">
          <p className="text-white/60" style={{ fontSize: "11.5px", lineHeight: 1.6, maxWidth: "62ch" }}>
            {BRANDING_PROMISE}
          </p>

          {BRANDING_STAGES.map((stage) => (
            <div key={stage.id} data-testid={`col-branding-stage-${stage.id}`}>
              <div className="flex items-baseline gap-2 pb-1.5 border-b border-white/[0.06]">
                <span className="text-[#8FA79B]" style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase" }}>
                  {stage.label}
                </span>
                <span className="text-white/35" style={{ fontSize: "10.5px" }}>{stage.caption}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pt-3">
                {stage.deliverables.map((d) => (
                  <div key={d.id} className="flex gap-2.5" data-testid={`col-branding-item-${d.id}`}>
                    <span className="text-[#8FA79B]/70 tabular-nums flex-shrink-0" style={{ fontSize: "10px", paddingTop: "2px" }}>
                      {String(BRANDING_DELIVERABLES.findIndex((x) => x.id === d.id) + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <p className="text-white/90" style={{ fontSize: "12px" }}>{d.title}</p>
                      <p className="text-white/45 mt-0.5" style={{ fontSize: "10.5px", lineHeight: 1.55 }}>{d.blurb}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div>
            <p className="text-[#8FA79B]" style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase" }}>How the engagement runs</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2.5">
              {BRANDING_PROCESS.map((st) => (
                <div key={st.step} className="pt-2" style={{ borderTop: "1.5px solid rgba(143,167,155,0.5)" }}>
                  <p className="text-[#8FA79B]/70" style={{ fontSize: "9.5px", letterSpacing: "0.16em" }}>{st.step}</p>
                  <p className="text-white mt-0.5" style={{ fontSize: "11.5px" }}>{st.title}</p>
                  <p className="text-white/45 mt-1" style={{ fontSize: "10px", lineHeight: 1.5 }}>{st.detail}</p>
                </div>
              ))}
            </div>
            <p className="text-white/35 mt-2" style={{ fontSize: "10px", lineHeight: 1.5 }}>{BRANDING_DURATION_NOTE}</p>
          </div>

          <div>
            <p className="text-[#8FA79B]" style={{ fontSize: "10px", letterSpacing: "0.18em", textTransform: "uppercase" }}>What is included</p>
            <ul className="pt-2 space-y-1.5 list-disc pl-4">
              {BRANDING_INCLUSIONS.map((n, i) => (
                <li key={i} className="text-white/45" style={{ fontSize: "10.5px", lineHeight: 1.55 }}>{n}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
// ─── Main component ────────────────────────────────────────────────────
export default function MarketingCollateralTab({
  marketing, setMarketing,
  collateral, setCollateral,
  marketingMath, collateralMath,
  // Marketing reaches the quote and the proposal only when the campaign is
  // active AND has a resolved cost-per-lead. This flag is the gap between the
  // two — toggled on, not yet priced, therefore not on the proposal.
  marketingNeedsPriceTier,
  cplTiers, // { preSale: [...], preLease: [...] }
  marketingConfig, collateralConfig,
  termMonths,
  // Flags the quote as having unsaved changes. Wired for the campaign-fee
  // overrides specifically — a hand-set price is money the rep must not lose
  // by navigating away.
  onDirty,
  // Proposal-download props (Phase 3 PDF system).
  proposalMode,        // 'vexos' | 'marketing' | 'combined'
  savedQuoteId,        // id of the persisted quote, or null
  onDownloadProposal,  // (quoteId, mode) => void
}) {
  const [mktOpen, setMktOpen] = useState(true);
  const [colOpen, setColOpen] = useState(true);
  const m = marketing;
  const c = collateral;
  const set = (key, v) => setMarketing((prev) => ({ ...prev, [key]: v }));
  const setC = (key, v) => setCollateral((prev) => ({ ...prev, [key]: v }));

  const activeTierList = m.campaignType === "pre-lease" ? cplTiers.preLease : cplTiers.preSale;

  return (
    <div className="space-y-8" data-testid="marketing-collateral-tab">
      {/* ─── Marketing Strategy & Lead Generation ─── */}
      <section
        className="rounded-2xl border border-white/[0.06] bg-white/[0.015] px-6 py-2"
        data-testid="marketing-section"
      >
        <SectionHeader
          Icon={Megaphone}
          title="Marketing Strategy & Lead Generation"
          open={mktOpen}
          onToggle={() => setMktOpen((v) => !v)}
          testId="marketing-section-header"
          right={
            <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <Toggle
                value={!!m.active}
                onChange={(v) => {
                  set("active", v);
                  if (v) setMktOpen(true);
                }}
                testId="marketing-active-toggle"
              />
              {m.active && (
                marketingNeedsPriceTier ? (
                  /*  The setup fee and the management fee are driven by the
                   *  Lead Gen Units tier, not by the price point, so they read
                   *  as real money even before a price point is picked. Showing
                   *  their sum as a monthly budget here is what made an
                   *  unfinished campaign look finished.  */
                  <span
                    className="px-3 py-1 rounded-full"
                    style={{ background: "rgba(240,180,41,0.12)", color: "#F0B429", fontSize: "12px", fontFamily: 'var(--font-sans)' }}
                    data-testid="marketing-incomplete-pill"
                  >
                    Not on proposal
                  </span>
                ) : (
                  <span
                    className="px-3 py-1 rounded-full"
                    style={{ background: "rgba(143,167,155,0.12)", color: "#8FA79B", fontSize: "12px", fontFamily: 'var(--font-sans)', fontVariantNumeric: "tabular-nums" }}
                  >
                    {fmtUSD(marketingMath.monthlyBudget)}/mo
                  </span>
                )
              )}
            </div>
          }
        />
        {mktOpen && m.active && (
          <div className="pb-6 pt-2 space-y-7">
            <div className="space-y-2">
              <p className="uppercase text-[#8FA79B]" style={{ fontSize: "10.5px", letterSpacing: "0.18em" }}>Campaign Type</p>
              <div className="grid grid-cols-2 gap-3">
                <TierTile active={m.campaignType === "pre-lease"} label="Pre-Lease" sub="Rental campaign" onClick={() => set("campaignType", "pre-lease")} testId="campaign-type-pre-lease" />
                <TierTile active={m.campaignType === "pre-sale"}  label="Pre-Sale"  sub="Sale campaign"   onClick={() => set("campaignType", "pre-sale")}  testId="campaign-type-pre-sale" />
              </div>
            </div>

            {marketingNeedsPriceTier && (
              <div
                className="rounded-xl px-4 py-3 flex items-start gap-3"
                style={{ background: "rgba(240,180,41,0.08)", border: "1px solid rgba(240,180,41,0.35)" }}
                data-testid="marketing-needs-price-tier"
              >
                <AlertTriangle size={15} style={{ color: "#F0B429", flexShrink: 0, marginTop: 1 }} />
                <p className="text-[12px] leading-relaxed" style={{ color: "rgba(255,255,255,0.75)" }}>
                  <span style={{ color: "#F0B429" }}>This campaign is not on the quote yet.</span>{" "}
                  Pick an {m.campaignType === "pre-lease" ? "average monthly rent" : "average price per unit"} below — it sets the
                  cost per lead. Until then the campaign is left off the proposal, the payment
                  milestones and the quote total, even with the toggle on.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <p
                className="uppercase"
                style={{ fontSize: "10.5px", letterSpacing: "0.18em", color: marketingNeedsPriceTier ? "#F0B429" : "#8FA79B" }}
              >
                {m.campaignType === "pre-lease" ? "Average Monthly Rent" : "Average Price Per Unit"}
                {marketingNeedsPriceTier && <span className="ml-2 normal-case tracking-normal text-[10.5px]">· required</span>}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {activeTierList.map((t) => (
                  <TierTile
                    key={t.id}
                    active={m.priceTier === t.id}
                    label={t.label}
                    sub={`${fmtUSD(t.rate)}/lead`}
                    onClick={() => set("priceTier", m.priceTier === t.id ? null : t.id)}
                    testId={`price-tier-${t.id}`}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <p
                  className="text-white/40 text-[10.5px] leading-snug"
                  data-testid="lead-gen-units-hint"
                >
                  <span className="text-[#8FA79B]/80">Note:</span> Lead Gen Units = units actively being marketed in this campaign (independent of VEXOS project size).
                  Drives the Lead Gen setup-fee tier only — VEXOS pricing is unaffected.
                </p>
              </div>
              <Slider label="Lead Gen Units" value={m.units} min={marketingConfig.unitsMin} max={marketingConfig.unitsMax} onChange={(v) => set("units", v)} testId="marketing-units-slider" />
              <Slider label="Sell-Out Timeline" value={m.sellOutMonths} min={marketingConfig.timelineMinMonths} max={marketingConfig.timelineMaxMonths} onChange={(v) => set("sellOutMonths", v)} suffix=" mo" testId="marketing-timeline-slider" />
              <Slider label="Sales Team Conversion Rate" value={m.conversionRate} min={marketingConfig.conversionMinPct} max={marketingConfig.conversionMaxPct} step={0.1} onChange={(v) => set("conversionRate", v)} suffix="%" testId="marketing-conversion-slider" />
            </div>

            {/* Live output card */}
            <div
              className="rounded-xl px-5 py-4"
              style={{ background: "rgba(0, 0, 0,0.55)", border: "1px solid rgba(143,167,155,0.3)" }}
              data-testid="marketing-output-card"
            >
              <div className="flex items-baseline justify-between mb-3">
                <p className="uppercase text-[#8FA79B]" style={{ fontSize: "11px", letterSpacing: "0.2em" }}>Recommended Monthly Budget</p>
                <p className="text-white tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontSize: "26px" }}>{fmtUSD(marketingMath.monthlyBudget)}</p>
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-[12px]">
                <span className="text-white/55" data-testid="marketing-setup-fee-label">
                  Setup Fee
                  {marketingMath.setupFeeOverride !== null && marketingMath.setupFeeOverride !== undefined ? (
                    <span className="ml-1.5 text-amber-300/80 text-[10.5px]" data-testid="marketing-setup-fee-manual">
                      (manual · rate card <span className="line-through text-white/30">{fmtUSD(marketingMath.setupFeeAuto)}</span>)
                    </span>
                  ) : marketingMath.campaignFeeTierLabel ? (
                    <span className="ml-1.5 text-white/35 text-[10.5px]">
                      ({marketingMath.campaignFeeTierLabel.replace("Tier ", "Tier ").split(" — ")[0]} · {marketingMath.campaignFeeTierUnits} units)
                    </span>
                  ) : null}
                </span>
                <EditableFee
                  value={marketingMath.setupFee}
                  auto={marketingMath.setupFeeAuto}
                  override={marketingMath.setupFeeOverride}
                  onCommit={(v) => { set("setupFeeOverride", v); onDirty?.(); }}
                  testId="marketing-setup-fee"
                />
                <span className="text-white/55">
                  Monthly Service Fee
                  {marketingMath.monthlyServiceFeeOverride !== null && marketingMath.monthlyServiceFeeOverride !== undefined && (
                    <span className="ml-1.5 text-amber-300/80 text-[10.5px]" data-testid="marketing-monthly-fee-manual">
                      (manual · rate card <span className="line-through text-white/30">{fmtUSD(marketingMath.monthlyServiceFeeAuto)}</span>)
                    </span>
                  )}
                </span>
                <EditableFee
                  value={marketingMath.monthlyServiceFee}
                  auto={marketingMath.monthlyServiceFeeAuto}
                  override={marketingMath.monthlyServiceFeeOverride}
                  onCommit={(v) => { set("monthlyServiceFeeOverride", v); onDirty?.(); }}
                  suffix="/mo"
                  testId="marketing-monthly-fee"
                />
                <span className="text-white/55">Monthly Ad Spend</span>     <span className="text-white text-right tabular-nums">{fmtUSD(marketingMath.monthlyAdSpend)}/mo</span>
                <span className="text-white/55">Est. Monthly Leads</span>   <span className="text-white text-right tabular-nums">{Math.round(marketingMath.monthlyLeads).toLocaleString()}</span>
                <span className="text-white/55">Total Est. Leads</span>     <span className="text-white text-right tabular-nums">{Math.round(marketingMath.totalLeads).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ─── Collateral Suite ─── */}
      <section
        className="rounded-2xl border border-white/[0.06] bg-white/[0.015] px-6 py-2"
        data-testid="collateral-section"
      >
        <SectionHeader
          Icon={Sparkles}
          title="Collateral Suite"
          open={colOpen}
          onToggle={() => setColOpen((v) => !v)}
          testId="collateral-section-header"
          right={
            <span
              className="px-3 py-1 rounded-full"
              style={{ background: "rgba(143,167,155,0.12)", color: "#8FA79B", fontSize: "12px", fontFamily: 'var(--font-sans)', fontVariantNumeric: "tabular-nums" }}
            >
              {fmtUSD(collateralMath.collateralTotal)}
            </span>
          }
        />
        {colOpen && (
          <div className="pb-6 pt-1 space-y-7">
            <p className="text-white/45 -mt-2" style={{ fontSize: "12px", lineHeight: 1.6 }}>
              Branding, digital presence, sales collateral, and AI-enhanced marketing content.
            </p>

            <SubSection label="Digital Presence">
              <ItemRow name="Landing Page"  desc="Single high-converting page for lead capture and campaign traffic." price={collateralConfig.landingPage}  value={c.landingPage}  onChange={(v) => setC("landingPage", v)}  testId="col-landingPage" />
              <ItemRow name="Full Website"  desc="Multi-page website with full structure and user journey."         price={collateralConfig.fullWebsite}  value={c.fullWebsite}  onChange={(v) => setC("fullWebsite", v)}  testId="col-fullWebsite" />
            </SubSection>

            <SubSection label="Branding & Positioning">
              <BrandingPackageRow price={collateralConfig.brandingPackage} value={c.brandingPackage} onChange={(v) => setC("brandingPackage", v)} testId="col-brandingPackage" />
              <ItemRow name="Investment Presentation"       desc="Investor-facing deck (up to 20 pages)."                                   price={collateralConfig.investmentPresentation} value={c.investmentPresentation} onChange={(v) => setC("investmentPresentation", v)} testId="col-investmentPresentation" />
            </SubSection>

            <SubSection label="Sales & Marketing Collateral">
              <ItemRow name="Development Flipbook"            desc="Comprehensive project sales/marketing book (40–50 pages)."        price={collateralConfig.developmentFlipbook} value={c.developmentFlipbook} onChange={(v) => setC("developmentFlipbook", v)} testId="col-developmentFlipbook" />
              <ItemRow name="Billboard Design (Static)"       desc="Billboard-ready static creative using project visuals."           price={collateralConfig.billboardStatic}     value={c.billboardStatic}     onChange={(v) => setC("billboardStatic", v)}     testId="col-billboardStatic" />
              <ItemRow name="Billboard Design (Animated)"     desc="Billboard-ready animated creative up to 10 seconds."              price={collateralConfig.billboardAnimated}   value={c.billboardAnimated}   onChange={(v) => setC("billboardAnimated", v)}   testId="col-billboardAnimated" />
              <ItemRow name="Unit Inventory Breakdown Sheet"  desc="Clean, structured unit matrix for sales teams and buyers."        price={collateralConfig.unitInventorySheet}  value={c.unitInventorySheet}  onChange={(v) => setC("unitInventorySheet", v)}  testId="col-unitInventorySheet" />
            </SubSection>

            <SubSection label="AI-Enhanced Marketing Content">
              <StepperRow name="Animated Renders (Social Media)" desc="Short-form animated clips from existing renderings for ads and social media." price={collateralConfig.animatedRendersPer}   value={c.animatedRendersQty}   onChange={(v) => setC("animatedRendersQty", v)}   testId="col-animatedRenders" />
              <StepperRow name="Render Enhancement"               desc="AI-enhanced improvements to existing renderings for realism and visual impact." price={collateralConfig.renderEnhancementPer} value={c.renderEnhancementQty} onChange={(v) => setC("renderEnhancementQty", v)} testId="col-renderEnhancement" />
            </SubSection>

            <SubSection label="AI Social Media Retainer">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
                {[
                  { id: "6-clips",  label: "6 clips/mo",  sub: "Monthly retainer for 6 AI-generated 15-second video clips",            rate: collateralConfig.retainer6 },
                  { id: "12-clips", label: "12 clips/mo", sub: "12 AI-generated 15-second clips · 10% volume discount",                rate: collateralConfig.retainer12 },
                  { id: "18-clips", label: "18 clips/mo", sub: "18 AI-generated 15-second clips · 15% volume discount",                rate: collateralConfig.retainer18 },
                ].map((opt) => (
                  <TierTile
                    key={opt.id}
                    active={c.socialMediaRetainerPackage === opt.id}
                    label={opt.label}
                    sub={`${fmtUSD(opt.rate)}/mo · ${opt.sub}`}
                    onClick={() => setC("socialMediaRetainerPackage", c.socialMediaRetainerPackage === opt.id ? null : opt.id)}
                    testId={`col-retainer-${opt.id}`}
                  />
                ))}
              </div>
            </SubSection>

            <div className="flex items-center justify-between pt-3 border-t border-white/[0.05]">
              <p className="text-white" style={{ fontSize: "13px", fontWeight: 400 }}>Collateral Suite Total</p>
              <p className="text-white tabular-nums" style={{ fontFamily: 'var(--font-sans)', fontSize: "20px" }} data-testid="collateral-total">
                {fmtUSD(collateralMath.collateralTotal)}
                {collateralMath.retainerMonthly > 0 && (
                  <span className="ml-2 text-white/55" style={{ fontSize: "12px", fontFamily: 'var(--font-sans)' }}>
                    + {fmtUSD(collateralMath.retainerMonthly)}/mo × {termMonths}mo
                  </span>
                )}
              </p>
            </div>
          </div>
        )}
      </section>

      {/* ─── Marketing Proposal Download ─── */}
      {(marketingMath?.cpl > 0 || collateralMath?.hasAny) && (
        <section
          className="rounded-2xl border border-[#8FA79B]/15 bg-[#8FA79B]/[0.03] px-6 py-5 flex items-center justify-between gap-4 flex-wrap"
          data-testid="marketing-proposal-download-row"
        >
          <div className="min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#8FA79B]/85 mb-1">Marketing Proposal</p>
            <p className="text-[13px] text-white">
              {proposalMode === "combined"
                ? "Marketing scope is included in the combined proposal."
                : "Generate a Marketing-only PDF for this campaign scope."}
            </p>
            {/*  Reachable when collateral alone carries the row: the campaign
             *  is toggled on but unpriced, so the proposal above it covers the
             *  collateral only. Saying so beats letting the rep read
             *  "included" and assume the campaign went with it.  */}
            {marketingNeedsPriceTier && (
              <p className="text-[11.5px] mt-1" style={{ color: "#F0B429" }} data-testid="marketing-download-incomplete-note">
                The lead-gen campaign is excluded — it still needs an {m.campaignType === "pre-lease" ? "average monthly rent" : "average price per unit"}.
              </p>
            )}
          </div>
          {proposalMode === "combined" ? (
            <button
              type="button"
              onClick={() => savedQuoteId && onDownloadProposal && onDownloadProposal(savedQuoteId, "combined")}
              disabled={!savedQuoteId}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] tracking-wide border border-[#8FA79B]/40 text-[#8FA79B] hover:bg-[#8FA79B]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="marketing-included-btn"
              title={savedQuoteId ? "Saves current state, then opens the combined proposal" : "Save the quote first"}
            >
              Included in the combined proposal →
            </button>
          ) : (
            <button
              type="button"
              onClick={() => savedQuoteId && onDownloadProposal && onDownloadProposal(savedQuoteId, "marketing")}
              disabled={!savedQuoteId}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] tracking-wide text-[#000000] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              style={{ background: savedQuoteId ? "#8FA79B" : "rgba(143,167,155,0.25)", fontWeight: 450 }}
              data-testid="marketing-download-btn"
              title={savedQuoteId ? "Saves current state, then opens the Marketing Proposal" : "Save the quote first"}
            >
              <Download className="w-3.5 h-3.5" />
              Download Marketing Proposal
            </button>
          )}
        </section>
      )}
    </div>
  );
}

function SubSection({ label, children }) {
  return (
    <div>
      <p className="uppercase text-[#8FA79B] mb-1" style={{ fontSize: "10.5px", letterSpacing: "0.18em" }}>{label}</p>
      <div>{children}</div>
    </div>
  );
}
